/**
 * Resilient JWT storage for FutureSelf.
 *
 * Why this exists:
 *   On iOS WKWebView, requests to `app.futureself.es` from the Capacitor
 *   `capacitor://localhost` origin are cross-origin. iOS ITP therefore
 *   treats the session cookie AND localStorage as third-party storage,
 *   and will clear them after a period of inactivity — logging the user
 *   out every time they relaunch the app once ITP has swept.
 *
 *   We mirror the JWT into `@capacitor/preferences`, which is backed by
 *   iOS `UserDefaults` / Android `SharedPreferences`. Those stores are
 *   OUTSIDE WKWebView, so ITP cannot touch them. On cold-start we
 *   rehydrate the token from Preferences back into `localStorage` so the
 *   rest of the app (authFetch wrapper, chunk uploads, etc.) continues
 *   to read from the single `localStorage` location it already uses.
 *
 *   On the web build this module is a no-op shim around `localStorage`
 *   — behaviour is unchanged.
 *
 *   Nothing in this module is specific to the token's shape: it is
 *   stored and retrieved as an opaque string so JWT rotation never
 *   requires a schema change here.
 */

import { isNativePlatform } from './native';

export const TOKEN_KEY = 'futureself_auth_token';

const safeLocal = {
  get: (key) => {
    try { return localStorage.getItem(key); } catch { return null; }
  },
  set: (key, value) => {
    try { localStorage.setItem(key, value); } catch { /* ignore */ }
  },
  remove: (key) => {
    try { localStorage.removeItem(key); } catch { /* ignore */ }
  },
};

// Lazy import so web bundles don't pull Capacitor plugin code.
const _lazyPrefs = async () => {
  if (!isNativePlatform()) return null;
  try {
    // Return the module, never the plugin proxy: resolving a promise with a
    // Capacitor proxy calls its `then` as a native method and hangs forever.
    return await import('@capacitor/preferences');
  } catch (e) {
    // Non-fatal: web bundles / older native builds without the plugin
    // simply fall back to localStorage.
    return null;
  }
};

/**
 * Persist the token. On native, writes to BOTH Preferences (survives
 * ITP) and localStorage (fast synchronous reads by the rest of the app).
 */
export const persistToken = async (token) => {
  if (!token) return;
  safeLocal.set(TOKEN_KEY, token);
  const Preferences = (await _lazyPrefs())?.Preferences;
  if (Preferences) {
    try { await Preferences.set({ key: TOKEN_KEY, value: token }); } catch { /* ignore */ }
  }
  await clearSignedOut();
};

// Explicit sign-out marker: while set, a leftover session cookie must not
// silently sign the user back in on the next app open.
const SIGNED_OUT_KEY = 'futureself_signed_out';

export const markSignedOut = async () => {
  safeLocal.set(SIGNED_OUT_KEY, '1');
  const Preferences = (await _lazyPrefs())?.Preferences;
  if (Preferences) {
    try { await Preferences.set({ key: SIGNED_OUT_KEY, value: '1' }); } catch { /* ignore */ }
  }
};

export const clearSignedOut = async () => {
  safeLocal.remove(SIGNED_OUT_KEY);
  const Preferences = (await _lazyPrefs())?.Preferences;
  if (Preferences) {
    try { await Preferences.remove({ key: SIGNED_OUT_KEY }); } catch { /* ignore */ }
  }
};

export const isSignedOut = async () => {
  if (safeLocal.get(SIGNED_OUT_KEY) === '1') return true;
  const Preferences = (await _lazyPrefs())?.Preferences;
  if (!Preferences) return false;
  try {
    const { value } = await Preferences.get({ key: SIGNED_OUT_KEY });
    return value === '1';
  } catch {
    return false;
  }
};

/**
 * Clear the token from BOTH stores. Called on explicit logout only.
 */
export const clearToken = async () => {
  safeLocal.remove(TOKEN_KEY);
  const Preferences = (await _lazyPrefs())?.Preferences;
  if (Preferences) {
    try { await Preferences.remove({ key: TOKEN_KEY }); } catch { /* ignore */ }
  }
};

/**
 * Cold-start rehydration. If localStorage is empty (ITP cleared it) but
 * Preferences has the token, restore it into localStorage. Returns the
 * token in-hand (or null) so the caller can decide whether to attempt a
 * `/auth/me` revalidation immediately.
 *
 * Safe on web — short-circuits via `isNativePlatform()` without touching
 * the plugin, so the web path remains a pure localStorage read.
 */
const REHYDRATE_TIMEOUT_MS = 3000;

// Bounded: a stalled native bridge resolves null so startup falls through to sign-in.
export const rehydrateToken = () => {
  const current = safeLocal.get(TOKEN_KEY);
  if (current) return Promise.resolve(current);
  return Promise.race([
    _readNativeToken().catch(() => null),
    new Promise((resolve) => setTimeout(() => resolve(null), REHYDRATE_TIMEOUT_MS)),
  ]);
};

const _readNativeToken = async () => {
  const Preferences = (await _lazyPrefs())?.Preferences;
  if (!Preferences) return null;
  try {
    const { value } = await Preferences.get({ key: TOKEN_KEY });
    if (value) {
      safeLocal.set(TOKEN_KEY, value);
      return value;
    }
  } catch { /* ignore */ }
  return null;
};
